/**
 * captureError — lightweight diagnostic logging helper.
 *
 * Every catch site that this is added to gets BOTH a tagged console.error (so
 * the failure is visible in the device WebView / browser console during
 * debugging) AND a guarded PostHog `captureException` (so a repeat failure in
 * production surfaces in error-tracking → the auto-fix monitoring loop).
 *
 * PostHog capture is guarded by `import.meta.env.PROD && posthog.__loaded`,
 * matching the existing pattern in `ComprehensiveErrorBoundary` and
 * `Login.tsx`. On web/dev, or if PostHog never loaded (e.g. blocked, native
 * build without the key), capture is silently skipped and only the console line
 * runs. This is an explicit, justified capture site — flows that were
 * previously `catch { navigate(...) }` with no logging (native OAuth, CNIC
 * upload) are now fully diagnosable.
 */
import posthog from "posthog-js";

/** Coerce any thrown value into an Error with a readable message.
 *  Plain objects previously stringified to "[object Object]", producing
 *  unactionable PostHog issues — extract message-like fields or JSON instead.
 *  Exported so other direct PostHog capture sites (e.g. the error boundary,
 *  whose `componentDidCatch` receives whatever was thrown — not guaranteed to
 *  be an Error) can share the same coercion instead of re-implementing it. */
export function toError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === "string") return new Error(error);
  try {
    const e = error as Record<string, unknown> | null;
    const msg = e && (e.message ?? e.error_description ?? e.error ?? e.msg);
    if (typeof msg === "string" && msg) return new Error(msg);
    return new Error(JSON.stringify(error)?.slice(0, 500) || String(error));
  } catch {
    return new Error(String(error));
  }
}

export function captureError(
  tag: string,
  error: unknown,
  context?: Record<string, unknown>,
): void {
  // Always log to the console with a searchable tag.
  // eslint-disable-next-line no-console
  console.error(`[${tag}]`, error, context ?? "");

  try {
    if (import.meta.env.PROD && posthog.__loaded) {
      posthog.captureException(toError(error), { tag, ...(context ?? {}) });
    }
  } catch {
    /* never let telemetry throw into the caller's catch path */
  }
}
